// src/pages/AdminConfig.jsx
import { useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase/firebase.js";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";

export default function AdminConfig() {
  // Solo para mostrar en UI y evitar warnings
  const [me, setMe] = useState(null);

  // Estado del doc config/global
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Formulario (con defaults seguros)
  const [form, setForm] = useState({
    diasCaducidadDefault: 30,
    precioPublicacionMXN: 149,
    webhooks: {
      inactivateWcMl: "",
    },
    seller: {
      // opcionales, si los quieres usar más adelante:
      sellerIdDefault: "",
      sucursalIdDefault: "",
    },
  });

  // Cargar usuario (solo UI)
  useEffect(() => {
    const u = auth.currentUser;
    if (u) setMe({ uid: u.uid, email: u.email || "" });
    else setMe(null);
  }, []);

  // Cargar doc config/global
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const ref = doc(db, "config", "global");
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() || {};
          setForm({
            diasCaducidadDefault: Number(data?.diasCaducidadDefault) || 30,
            precioPublicacionMXN: Number(data?.precioPublicacionMXN) || 149,
            webhooks: {
              inactivateWcMl: data?.webhooks?.inactivateWcMl || "",
            },
            seller: {
              sellerIdDefault: data?.seller?.sellerIdDefault || "",
              sucursalIdDefault: data?.seller?.sucursalIdDefault || "",
            },
          });
          setExists(true);
        } else {
          setExists(false);
        }
      } catch (e) {
        console.error("[AdminConfig] cargar:", e);
        alert("No se pudo cargar la configuración.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // evita incluir `db` para no gatillar warnings

  const esNuevo = useMemo(() => !exists, [exists]);

  // Handlers
  const onChange = (e) => {
    const { name, value, type } = e.target;
    const val = type === "number" ? Number(value) : value;

    if (name.startsWith("webhooks.")) {
      const field = name.split(".")[1];
      setForm((v) => ({ ...v, webhooks: { ...v.webhooks, [field]: val } }));
      return;
    }
    if (name.startsWith("seller.")) {
      const field = name.split(".")[1];
      setForm((v) => ({ ...v, seller: { ...v.seller, [field]: val } }));
      return;
    }

    setForm((v) => ({ ...v, [name]: val }));
  };

  const guardar = async () => {
    try {
      setSaving(true);
      const ref = doc(db, "config", "global");

      const payload = {
        diasCaducidadDefault:
          Number(form.diasCaducidadDefault) > 0
            ? Number(form.diasCaducidadDefault)
            : 30,
        precioPublicacionMXN:
          Number(form.precioPublicacionMXN) >= 0
            ? Number(form.precioPublicacionMXN)
            : 0,
        webhooks: {
          inactivateWcMl: (form.webhooks.inactivateWcMl || "").trim(),
        },
        seller: {
          sellerIdDefault: (form.seller.sellerIdDefault || "").trim(),
          sucursalIdDefault: (form.seller.sucursalIdDefault || "").trim(),
        },
        updatedAt: new Date().toISOString(),
      };

      if (esNuevo) {
        await setDoc(ref, {
          ...payload,
          createdAt: new Date().toISOString(),
        });
        setExists(true);
      } else {
        await updateDoc(ref, payload);
      }

      alert("Configuración guardada.");
    } catch (e) {
      console.error("[AdminConfig] guardar:", e);
      alert(e.message || "No se pudo guardar la configuración.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-600">Cargando…</div>;

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900">
      {/* Topbar */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white flex justify-between items-center">
        <div className="font-semibold">Configuración Global</div>
        <div className="text-sm text-gray-600">Admin · {me?.email || ""}</div>
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Panel info (izquierda) */}
        <aside className="lg:col-span-1">
          <div className="rounded border border-gray-200 bg-white p-3">
            <h3 className="font-semibold mb-1">Documento</h3>
            <div className="text-sm text-gray-600">Ruta: <code>config/global</code></div>
            <div className="mt-2 text-sm">
              Estado:{" "}
              <span
                className={`px-2 py-0.5 rounded border ${
                  exists
                    ? "border-emerald-500 text-emerald-700"
                    : "border-amber-500 text-amber-700"
                }`}
              >
                {exists ? "Existe" : "No existe (se creará)"}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Aquí se definen valores por defecto de publicación, precios y
              endpoints de webhooks.
            </p>
          </div>
        </aside>

        {/* Form principal (derecha) */}
        <main className="lg:col-span-2">
          <div className="rounded border border-gray-200 bg-white p-4 space-y-6">
            {/* Publicación */}
            <section>
              <h4 className="font-semibold mb-3">Publicación</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-sm text-gray-600">Días de caducidad (default)</label>
                  <input
                    type="number"
                    name="diasCaducidadDefault"
                    value={form.diasCaducidadDefault}
                    onChange={onChange}
                    className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Precio publicación (MXN)</label>
                  <input
                    type="number"
                    name="precioPublicacionMXN"
                    value={form.precioPublicacionMXN}
                    onChange={onChange}
                    className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                  />
                </div>
              </div>
            </section>

            {/* Webhooks */}
            <section>
              <h4 className="font-semibold mb-3">Webhooks</h4>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="text-sm text-gray-600">Inactivate Woo/ML</label>
                  <input
                    name="webhooks.inactivateWcMl"
                    value={form.webhooks.inactivateWcMl}
                    onChange={onChange}
                    placeholder="https://tuservicio/endpoint/inactivate"
                    className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                  />
                </div>
              </div>
            </section>

            {/* Seller (opcional) */}
            <section>
              <h4 className="font-semibold mb-3">Seller (opcional)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-600">sellerIdDefault</label>
                  <input
                    name="seller.sellerIdDefault"
                    value={form.seller.sellerIdDefault}
                    onChange={onChange}
                    placeholder=""
                    className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">sucursalIdDefault</label>
                  <input
                    name="seller.sucursalIdDefault"
                    value={form.seller.sucursalIdDefault}
                    onChange={onChange}
                    placeholder="cdmx, gdl, mty…"
                    className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Estos campos son auxiliares por si quieres inferir valores por
                defecto en flujos futuros.
              </p>
            </section>

            {/* Guardar */}
            <div className="flex items-center gap-3">
              <button
                onClick={guardar}
                disabled={saving}
                className="px-4 py-2 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
              >
                {saving ? "Guardando…" : esNuevo ? "Crear configuración" : "Guardar cambios"}
              </button>
              <span className="text-sm text-gray-600">Doc: <code>global</code></span>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}